import * as React from "react";
import { RefObject, useContext, useEffect } from "react";
import Modal from "@app/shared/Modals/Modal";
import Portal from "@app/shared/Portal";
import { FlyoutContent } from "@app/shared/Flyouts/flyoutContent";
import { ModalContent } from "@app/shared/Modals";
import { FlyoutContext } from "@app/shared/Flyouts/FlyoutContext";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import cx from "classnames";
import { QESButton } from "@app/shared/qes-standards/Button";

interface FlyoutProps {
  asDrawer?: boolean;
  container?: Element;
  content: FlyoutContent;
  modalContent?: Partial<ModalContent>;
  panelId?: string;
  triggerElement?: RefObject<HTMLElement>;
  size?: "20" | "40" | "60" | "80" | "90";
}

const Flyout: React.FC<FlyoutProps> = (props: FlyoutProps) => {
  const flyoutContext = useContext(FlyoutContext);

  useEffect(() => {
    flyoutContext.setTriggerElement(props.triggerElement);
  }, [props.triggerElement]);

  return (
    <>
      <Modal
        content={{
          body: props.modalContent?.body || undefined,
          cancelButtonText: props.modalContent?.cancelButtonText || undefined,
          confirmButtonAction: flyoutContext.confirmClose,
          confirmButtonText: props.modalContent?.confirmButtonText || undefined,
          title: props.modalContent?.title || undefined,
        }}
        closeModal={flyoutContext.cancelClose}
        isVisible={flyoutContext.isModalWarningVisible}
        triggerElement={flyoutContext.firstFocusableElRef}
      />
      {flyoutContext.isFlyoutMounted && (
        <Portal container={props.container} hideBodyScroll={true}>
          {/**
         * not rendering anything here while not visible is the easiest
         * way to ensure none of this can be focused while the sidepanel isn't on screen
         */}
          <>
            <div
              id={props.panelId}
              className={cx({
                "as-drawer": props.asDrawer,
                "new-flyout": true,
                [`flyout-${props.size ? props.size : "60"}`]: true,
                "new-flyout-closed": true
              })}
              ref={flyoutContext.flyoutRef}
            >
              <>
                {!props.content.customHeader && (
                  <div className="flyout-heading">
                    <div className={cx({ "as-drawer": props.asDrawer, "flyout-heading-content w-100": true })}>
                      <div>
                        <h3 className="lead">{flyoutContext.title || props.content.title}</h3>
                        {props.content.subTitle && <p className="mt-1">{props.content.subTitle}</p>}
                      </div>
                      <div className="top-action-items">{props.content.topActionItems}</div>
                    </div>
                  </div>
                )}
                <QESButton qesTestId="flyout-close-button" className="flyout-close-button" qesType="secondary" onClick={flyoutContext.handleCloseFlyout} aria-label="Close Flyout">
                  <QesIcon iconName={QesIcons.X} description="Close" width="36" />
                </QESButton>
                <div className="flyout-body" ref={flyoutContext.flyoutBodyRef}>
                  <>
                    {props.content.body}
                  </>
                </div>
              </>
            </div>
            <div className="flyout-backdrop" data-testid="flyout-backdrop" onClick={flyoutContext.handleCloseFlyout} />
          </>
        </Portal>
      )}
    </>
  )
}

export default Flyout;